import type { ReactNode } from 'react';
import { Alert, type AlertSeverity } from '@tempmee/react';
import { pluralize } from '~/utils/String';
import { IShiftAlertContext, resolveShiftAlert, TShiftAlertKind } from '../utils/shiftAlerts';

export interface IShiftAlertSlotProps extends IShiftAlertContext {
  /**
   * The Counter Adjustment panel, rendered when the counter offer wins the slot.
   * It lives here rather than beside this component so that one place decides
   * what occupies the slot — otherwise "at most one alert" would depend on two
   * call sites agreeing.
   */
  counterOfferAlert?: ReactNode;
}

/**
 * The Confirm Candidates pages' single alert slot.
 *
 * Renders exactly one thing or nothing — {@link resolveShiftAlert} decides which
 * — and renders none of it clickable. The legacy screen wired three of these
 * banners to something: the payment banner opened an add-payment popup, and both
 * charge banners were collapsible buttons hiding the very sentence the office
 * needed. Here the copy is simply shown in full, so there is nothing left to
 * click and no way to miss it.
 */
export const ShiftAlertSlot = ({ counterOfferAlert, ...context }: IShiftAlertSlotProps) => {
  const kind = resolveShiftAlert(context);

  if (kind === null) {
    return null;
  }

  if (kind === 'counter-offer') {
    return <>{counterOfferAlert}</>;
  }

  const { severity, title, description } = ALERT_CONTENT[kind]({
    competingOffersCount: context.competingOffersCount ?? 0,
  });

  return (
    <Alert
      severity={severity}
      title={title}
      description={description}
      data-testid={`shift-alert-${kind}`}
      data-cy={`shift-alert-${kind}`}
    />
  );
};

interface IAlertContentArgs {
  competingOffersCount: number;
}

type TAlertContent = {
  severity: AlertSeverity;
  /** The bold first line. Only the CTA alert has one. */
  title?: ReactNode;
  description: ReactNode;
};

/**
 * Copy per alert kind, carried over from the banners the redesign replaces so the
 * wording an office already recognises does not change with the layout.
 *
 * `counter-offer` has no entry: it is a panel of terms, supplied by the caller,
 * not a line of copy.
 */
const ALERT_CONTENT: Record<
  Exclude<TShiftAlertKind, 'counter-offer'>,
  (args: IAlertContentArgs) => TAlertContent
> = {
  'payment-invalid': () => ({
    severity: 'error',
    description:
      'Your payment method is either missing or expired. Please update it to unlock candidate names and send invites to professionals.',
  }),

  // `cta` is the design's severity here — the lightbulb, not the info circle:
  // this is a nudge to act, not a system state. It is the one alert on the page
  // that carries a title, which is why `title` is optional above.
  'competing-offers': ({ competingOffersCount }) => ({
    severity: 'cta',
    title: 'Confirm now to secure coverage',
    description: `Professional has applied to ${competingOffersCount} other ${pluralize(
      'shift',
      competingOffersCount,
    )} the same day.`,
  }),
};
